import { useSyncExternalStore } from 'react';
import newestMarker from 'virtual:bms2-changelog-marker';
import { WHATS_NEW_SEEN_KEY } from '@bms2/shared';
import { devicePreferenceStore, readPreference, writePreference } from './devicePreference';

/**
 * The browser's memory of the newest change-log entry it has shown (piece 12,
 * decision 0636). 🔒 It is this browser's and nobody else's: no schema column
 * and no Settings key holds it. 💰 So a second device shows the dot once more,
 * until the What's new page is opened there too.
 *
 * 🔨 A store that is absent, blocked or throws reads as "never visited", which
 * draws the dot. The page is the one writer, and the header listens, so opening
 * the page clears the dot in the same tab without a reload.
 */
const SEEN_CHANGED = 'bms2:whats-new-seen';

export function readWhatsNewSeen(store: Pick<Storage, 'getItem'> | null = devicePreferenceStore()): string | null {
  return readPreference<string | null>(WHATS_NEW_SEEN_KEY, (raw) => raw, null, store);
}

export function rememberWhatsNewSeen(marker: string, store: Pick<Storage, 'setItem'> | null = devicePreferenceStore()): void {
  writePreference(WHATS_NEW_SEEN_KEY, marker, store);
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SEEN_CHANGED));
}

function subscribe(changed: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(SEEN_CHANGED, changed);
  // Another tab of the same browser opening the page clears this one's dot too.
  window.addEventListener('storage', changed);
  return () => {
    window.removeEventListener(SEEN_CHANGED, changed);
    window.removeEventListener('storage', changed);
  };
}

/**
 * Whether the avatar and the menu item carry the dot: there is a newest entry this browser has not been shown.
 * 🔨 The newest entry's marker is written at build time (T3600), so the 780 kB of entries load with the What's new page
 * and not with the first download. A log with no entry has the empty marker and draws no dot.
 */
export function useWhatsNewUnseen(marker: string = newestMarker): boolean {
  const seen = useSyncExternalStore(subscribe, () => readWhatsNewSeen(), () => null);
  return marker !== '' && marker !== seen;
}
